import DOMPurify from 'dompurify';
import { decode } from 'he';

export function cleanEmailHTML(rawHTML) {
    if (!rawHTML || typeof rawHTML !== 'string') return '';

    const minified = minifyHTML(rawHTML);
    const decoded = decode(minified);

    const sanitized = DOMPurify.sanitize(decoded, {
        ALLOWED_TAGS: [
            'p', 'b', 'i', 'u', 'strong', 'em', 'ul', 'li', 'ol',
            'br', 'span', 'div', 'a', 'img',
            'table', 'tr', 'td', 'th', 'thead', 'tbody', 'tfoot',
            'style', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'br', 'colgroup'
        ],
        ALLOWED_ATTR: [
            'style', 'href', 'src', 'alt', 'title', 'width', 'height',
            'cellpadding', 'cellspacing', 'border', 'align', 'valign',
            'colspan', 'rowspan', 'bgcolor', 'background', 'class', 'id', 'type', 'target', 'rel'
        ],
    });

    const normalized = sanitized
        .replace(/(\d+(\.\d+)?)pt/g, (_, num) =>
            `${Math.round(parseFloat(num) * 1.333)}px`
        )

        // empty <p>
        .replace(/<p([^>]*)>(\s|&nbsp;)*<\/p>/gi, (_, attrs) => {
            const style = mergeStyles(extractStyle(attrs), 'margin: 0 0 0 0; line-height: 1.5;');
            return `<p style="${style}"><br></p>`;
        })

        // all <p>
        .replace(/<p([^>]*)>/gi, (_, attrs) => {
            const style = mergeStyles(extractStyle(attrs), 'margin: 0 0 0 0; line-height: 1.5;');
            return `<p style="${style}">`;
        })

        // all <span>
        .replace(/<span([^>]*)>/gi, (_, attrs) => {
            const cleaned = filterSpanStyles(extractStyle(attrs));
            return cleaned ? `<span style="${cleaned}">` : `<span>`;
        })

        // all <ol>
        .replace(/<ol([^>]*)>/gi, (_, attrs) => {
            const style = mergeStyles(extractStyle(attrs), 'margin-bottom: 0;');
            return `<ol style="${style}">`;
        })

        // all <ul>
        .replace(/<ul([^>]*)>/gi, (_, attrs) => {
            const style = mergeStyles(extractStyle(attrs), 'margin-bottom: 0;');
            return `<ul style="${style}">`;
        });


    const final = wrapWithHtmlScaffold(normalized);
    return final;
}

function minifyHTML(html = '') {
    return html
        .replace(/\n+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .replace(/>\s+</g, '><')
        .replace(/\s*=\s*/g, '=')
        .trim();
}

function extractStyle(attrStr = '') {
    const match = attrStr.match(/style\s*=\s*(["'])([\s\S]*?)\1/i);
    return match ? match[2].replace(/\s+/g, ' ').trim() : '';
}

function mergeStyles(existing = '', enforced = '') {
    const map = {};

    const collect = (str) => {
        const rules = str.split(/;(?![^()]*\))/);
        for (const rule of rules) {
            const [prop, ...rest] = rule.split(':');
            if (!prop || !rest.length) continue;
            const value = rest.join(':').trim();
            const key = prop.trim().toLowerCase();
            if (key && value) map[key] = value;
        }
    };

    collect(existing);
    collect(enforced);

    return Object.entries(map)
        .map(([prop, value]) => `${prop}: ${value}`)
        .join('; ');
}

function filterSpanStyles(styleStr = '') {
    const allowed = [
        'color',
        'font-weight',
        'font-style',
        'text-decoration',
        'font-family',
        'font-size'
    ];
    const out = [];

    styleStr.split(';').forEach(rule => {
        const [prop, value] = rule.split(':').map(s => s?.trim());
        if (allowed.includes(prop)) {
            out.push(`${prop}: ${value}`);
        }
    });

    return out.join('; ');
}

function wrapWithHtmlScaffold(innerHTML = '') {
    const hasDoctype = /<!doctype/i.test(innerHTML);
    //const hasHtml = /<html[\s>]/i.test(innerHTML);
    const hasHead = /<head[\s>]/i.test(innerHTML);
    const hasBody = /<body[\s>]/i.test(innerHTML);

    const metaTags = `
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
`;

    const headOpen = `<head>${metaTags}</head>`;
    const bodyOpen = `<body style="margin:0; padding:0; background-color:white;">`;

    const wrappedHTML = `
${hasDoctype ? '' : '<!DOCTYPE html>'}
<html lang="en">
${hasHead ? '' : headOpen}
${hasBody ? '' : bodyOpen}
${innerHTML}
${hasBody ? '' : '</body>'}
</html>
`.trim();

    return wrappedHTML;
}

export function cleanForTinyMCE(html) {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const blockSel = [
        'p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tfoot',
        'tr', 'td', 'th', 'blockquote', 'pre', 'hr', 'section',
        'article', 'aside', 'figure', 'figcaption'
    ].join(',');

    // 1) Unwrap builder wrappers (keep their children, drop the wrapper)
    doc.querySelectorAll('[data-component], .text, .image').forEach(el => {
        if (el.tagName === 'IMG') return; // don't unwrap actual images
        const frag = doc.createDocumentFragment();
        while (el.firstChild) frag.appendChild(el.firstChild);
        el.replaceWith(frag);
    });

    // 2) Unwrap inline elements that (even deeply) contain block elements
    const inlineSel = 'span,em,strong,b,i,u,small,mark,code,sup,sub,kbd,samp';
    doc.querySelectorAll(inlineSel).forEach(el => {
        if (el.querySelector(blockSel)) {
            const frag = doc.createDocumentFragment();
            while (el.firstChild) frag.appendChild(el.firstChild);
            el.replaceWith(frag);
        }
    });

    // 3) Fix <img> attributes
    doc.querySelectorAll('img').forEach(img => {
        const h = img.getAttribute('height');
        if (h && isNaN(parseInt(h, 10))) img.removeAttribute('height');
        const w = img.getAttribute('width');
        if (w && isNaN(parseInt(w, 10))) img.removeAttribute('width');
        img.style.height = 'auto';
        if (!img.style.maxWidth) img.style.maxWidth = '100%';
    });

    // 4) Remove empty id/class/style and trim noisy styles
    doc.querySelectorAll('*').forEach(el => {
        const style = el.getAttribute('style');
        if (style) {
            el.style.removeProperty('word-break');
            if (!el.getAttribute('style')) el.removeAttribute('style');
        }
        ['id', 'class', 'style'].forEach(attr => {
            const v = el.getAttribute(attr);
            if (v !== null && !v.trim()) el.removeAttribute(attr);
        });
    });

    // 5) Remove trailing <br> in <p>
    doc.querySelectorAll('p').forEach(p => {
        while (p.lastChild && p.lastChild.nodeName === 'BR') p.removeChild(p.lastChild);
    });

    // 6) Strip HTML comments
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_COMMENT);
    const toRemove = [];
    while (walker.nextNode()) toRemove.push(walker.currentNode);
    toRemove.forEach(n => n.parentNode.removeChild(n));

    return doc.body.innerHTML;
}
